// src/pages/markets/board/state/useBoardGroups.ts
//
// The `board_groups` collection: one document per trader group holding what the
// board calls it, what it says about itself, and whether the picker offers it.
//
// One document per group rather than one document holding a map, which is what
// lets a visibility toggle be a single-field write. The hook this replaces had
// to compose its next value from the react-query cache and hand-roll a
// per-group rollback, because two toggles in flight against one shared array
// would otherwise lose one of them. None of that is needed here.
//
// Only the board root imports this module; everything else reads the value
// through boardGroupsContext, which is why Firestore and react-admin stop here.

import { useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useGetIdentity } from 'react-admin';
import {
    collection,
    doc,
    getDocs,
    getFirestore,
    setDoc,
} from 'firebase/firestore';
import { useAccessFeatures } from '../../../../auth/useAccessFeatures';
import {
    BOARD_GROUPS_VERSION,
    collidingLabels,
    isGroupOffered,
    resolveGroup,
    resolveGroups,
    titleFor,
    type BoardGroupDoc,
} from './boardGroups';
import type { BoardGroupsApi, BoardGroupText } from './boardGroupsContext';
import type { BoardSchema } from '../api/boardTypes';

export const BOARD_GROUPS_COLLECTION = 'board_groups';
export const BOARD_GROUPS_QUERY_KEY = ['board_groups'] as const;

type DocMap = Record<string, BoardGroupDoc>;

async function fetchGroupDocs(): Promise<DocMap> {
    const snap = await getDocs(
        collection(getFirestore(), BOARD_GROUPS_COLLECTION)
    );
    const out: DocMap = {};
    snap.forEach(d => {
        out[d.id] = (d.data() ?? {}) as BoardGroupDoc;
    });
    return out;
}

export function useBoardGroupsValue(
    schema: BoardSchema | undefined
): BoardGroupsApi {
    const { identity } = useGetIdentity();
    const userId = identity?.id ? String(identity.id) : null;
    const access = useAccessFeatures();
    const isAdmin = access.role === 'admin';
    const qc = useQueryClient();

    const query = useQuery<DocMap>({
        queryKey: BOARD_GROUPS_QUERY_KEY,
        queryFn: fetchGroupDocs,
        staleTime: Infinity,
        gcTime: Infinity,
        refetchOnWindowFocus: false,
        retry: false,
    });

    const docs = query.data;
    const schemaGroups = schema?.groups;

    const all = useMemo(
        () => resolveGroups(schemaGroups ?? [], docs ?? {}),
        [schemaGroups, docs]
    );

    // Computed over the groups on offer, so hiding one half of a collision
    // resolves it rather than leaving both suffixed.
    const collisions = useMemo(() => collidingLabels([...all.values()]), [all]);

    const failOpen = query.isError;

    const resolve = useCallback(
        (group: string) => all.get(group) ?? resolveGroup(group, undefined),
        [all]
    );

    const titleOf = useCallback(
        (group: string) => titleFor(group, all.get(group), collisions, isAdmin),
        [all, collisions, isAdmin]
    );

    const offered = useCallback(
        (group: string) => isGroupOffered(group, isAdmin),
        [isAdmin]
    );

    // One write path for both the visibility toggle and the text editor. It
    // merges, and it rolls back only the fields the patch actually touched, so
    // a failed rename cannot revert a visibility toggle made while it was in
    // flight — or the other way round.
    const writeGroup = useCallback(
        async (group: string, patch: BoardGroupDoc) => {
            // The rules reject a non-admin's write anyway; skip the round trip
            // rather than let it fail visibly.
            if (!isAdmin || !userId) return;
            // Gated on a read that SUCCEEDED, not one that merely settled. On a
            // failed read every group reads as uncurated, and writing from that
            // would save a guess over what the desk had curated.
            if (!query.isSuccess) return;

            const previous =
                qc.getQueryData<DocMap>(BOARD_GROUPS_QUERY_KEY) ?? {};
            const stamped: BoardGroupDoc = {
                ...patch,
                updated_at: new Date().toISOString(),
                updated_by: userId,
            };
            // Optimistic: the panel shows the new text at once. Only this
            // group's document is touched, so a concurrent edit to another
            // group cannot be lost.
            qc.setQueryData<DocMap>(BOARD_GROUPS_QUERY_KEY, {
                ...previous,
                [group]: { ...previous[group], ...stamped },
            });

            try {
                await setDoc(
                    doc(getFirestore(), BOARD_GROUPS_COLLECTION, group),
                    { version: BOARD_GROUPS_VERSION, ...stamped },
                    { merge: true }
                );
            } catch (err) {
                const latest =
                    qc.getQueryData<DocMap>(BOARD_GROUPS_QUERY_KEY) ?? {};
                const restored: Record<string, unknown> = { ...latest[group] };
                const before = (previous[group] ?? {}) as Record<
                    string,
                    unknown
                >;
                for (const field of Object.keys(patch)) {
                    restored[field] = before[field];
                }
                qc.setQueryData<DocMap>(BOARD_GROUPS_QUERY_KEY, {
                    ...latest,
                    [group]: restored as BoardGroupDoc,
                });
                throw err;
            }
        },
        [isAdmin, qc, query.isSuccess, userId]
    );

    const setVisible = useCallback(
        (group: string, visible: boolean) => writeGroup(group, { visible }),
        [writeGroup]
    );

    const saveText = useCallback(
        (group: string, text: BoardGroupText) => {
            // A group with no name is unpickable — the menu row would be blank
            // — so this is refused rather than written and worked around at
            // read time. The editor disables Save for the same reason; this is
            // the guard for every other caller.
            const label = text.label.trim();
            if (!label) {
                return Promise.reject(new Error('A group needs a name.'));
            }
            return writeGroup(group, {
                label,
                one_liner: text.oneLiner.trim(),
                description: text.description.trim(),
            });
        },
        [writeGroup]
    );

    return useMemo(
        () => ({
            resolve,
            titleOf,
            all,
            isAdmin,
            isLoaded: query.isSuccess || query.isError,
            isSuccess: query.isSuccess,
            failOpen,
            offered,
            setVisible,
            saveText,
        }),
        [
            resolve,
            titleOf,
            all,
            isAdmin,
            query.isSuccess,
            query.isError,
            failOpen,
            offered,
            setVisible,
            saveText,
        ]
    );
}
